/**
 * Client-side video compression using browser MediaRecorder API.
 * Re-encodes video at lower bitrate through canvas + MediaRecorder.
 * No external dependencies required.
 */

const TARGET_MAX_SIZE = 48 * 1024 * 1024; // 48MB target (under 50MB limit)

/**
 * Compress a video File to fit under the size limit.
 * @param {File} file - Original video file
 * @param {function} onProgress - Progress callback (0-100)
 * @returns {Promise<File>} - Compressed video file (or original if already small enough)
 */
export async function compressVideo(file, onProgress = () => {}) {
  // If already under limit, skip compression
  if (file.size <= TARGET_MAX_SIZE) {
    onProgress(100);
    return file;
  }

  onProgress(5);

  // Create a video element to read the source
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;

  const videoUrl = URL.createObjectURL(file);

  try {
    // Load video metadata
    await new Promise((resolve, reject) => {
      video.onloadedmetadata = resolve;
      video.onerror = () => reject(new Error('Could not load video for compression'));
      video.src = videoUrl;
    });

    const duration = video.duration;
    const originalWidth = video.videoWidth;
    const originalHeight = video.videoHeight;

    // Calculate target resolution — scale down if very large
    // Aim for 720p max for analysis (horse movement doesn't need 4K)
    let targetWidth = originalWidth;
    let targetHeight = originalHeight;
    const maxDimension = 1280;

    if (originalWidth > maxDimension || originalHeight > maxDimension) {
      const scale = maxDimension / Math.max(originalWidth, originalHeight);
      targetWidth = Math.round(originalWidth * scale);
      targetHeight = Math.round(originalHeight * scale);
      // Ensure even dimensions (required by most codecs)
      targetWidth = targetWidth % 2 === 0 ? targetWidth : targetWidth + 1;
      targetHeight = targetHeight % 2 === 0 ? targetHeight : targetHeight + 1;
    }

    // Calculate target bitrate based on file size budget
    // Target: ~45MB for the compressed file
    const targetBytes = TARGET_MAX_SIZE * 0.9; // 90% of limit for safety
    const targetBitrate = Math.floor((targetBytes * 8) / duration); // bits per second
    // Clamp between 500kbps and 4Mbps
    const bitrate = Math.min(4_000_000, Math.max(500_000, targetBitrate));

    onProgress(10);

    // Set up canvas for re-encoding
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');

    // Choose codec — prefer VP9 for better compression, fall back to VP8, then H.264
    let mimeType = 'video/webm;codecs=vp9';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm;codecs=vp8';
    }
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm';
    }
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/mp4';
    }

    // Capture stream from canvas
    const stream = canvas.captureStream(30); // 30 fps

    // Try to capture audio too
    let audioCtx, audioSource, audioDest;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioSource = audioCtx.createMediaElementSource(video);
      audioDest = audioCtx.createMediaStreamDestination();
      audioSource.connect(audioDest);
      // Add audio track to our stream
      audioDest.stream.getAudioTracks().forEach(track => stream.addTrack(track));
    } catch (audioErr) {
      // Audio capture not critical — continue without it
      console.warn('Audio capture skipped:', audioErr.message);
    }

    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: bitrate,
    });

    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    // Start recording and play video through canvas
    const compressionComplete = new Promise((resolve, reject) => {
      recorder.onstop = () => resolve();
      recorder.onerror = (e) => reject(e.error || new Error('Recording failed'));
    });

    recorder.start(1000); // Collect data every second
    video.currentTime = 0;

    await new Promise((resolve) => {
      video.onseeked = resolve;
      video.currentTime = 0;
    });

    await video.play();

    // Draw frames to canvas while video plays
    const drawFrame = () => {
      if (video.paused || video.ended) return;

      ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

      // Update progress based on video position
      const progress = 10 + Math.round((video.currentTime / duration) * 80);
      onProgress(Math.min(progress, 90));

      requestAnimationFrame(drawFrame);
    };

    drawFrame();

    // Wait for video to finish playing
    await new Promise((resolve) => {
      video.onended = resolve;
      // Safety timeout: 2x video duration + 10s
      setTimeout(resolve, (duration * 2 + 10) * 1000);
    });

    video.pause();
    recorder.stop();

    await compressionComplete;

    onProgress(95);

    // Clean up audio context
    if (audioCtx) {
      try { audioCtx.close(); } catch (_) {}
    }

    // Create compressed file
    const blob = new Blob(chunks, { type: mimeType });
    const ext = mimeType.includes('webm') ? 'webm' : 'mp4';
    const compressedName = file.name.replace(/\.[^.]+$/, `-compressed.${ext}`);
    const compressedFile = new File([blob], compressedName, {
      type: mimeType.split(';')[0],
      lastModified: Date.now(),
    });

    onProgress(100);

    console.log(
      `Video compressed: ${(file.size / 1024 / 1024).toFixed(1)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(1)}MB ` +
      `(${originalWidth}x${originalHeight} → ${targetWidth}x${targetHeight}, ${Math.round(bitrate / 1000)}kbps)`
    );

    // If compression didn't actually help, return original
    if (compressedFile.size >= file.size) {
      console.warn('Compression did not reduce size — using original');
      return file;
    }

    return compressedFile;
  } finally {
    URL.revokeObjectURL(videoUrl);
  }
}
